/**
 * ResponsiveDialog — Dialog on desktop, Drawer on mobile.
 *
 * Drop-in replacement for Dialog that automatically switches to a
 * bottom Drawer (covering 4/5 of viewport) below the md breakpoint.
 *
 * Usage:
 *   import {
 *     ResponsiveDialog,
 *     ResponsiveDialogContent,
 *     ResponsiveDialogHeader,
 *     ResponsiveDialogTitle,
 *     ResponsiveDialogDescription,
 *     ResponsiveDialogFooter,
 *     ResponsiveDialogClose,
 *   } from '@/components/ui/responsive-dialog'
 *
 *   <ResponsiveDialog open={open} onOpenChange={setOpen}>
 *     <ResponsiveDialogContent>
 *       <ResponsiveDialogHeader>
 *         <ResponsiveDialogTitle>Title</ResponsiveDialogTitle>
 *         <ResponsiveDialogDescription>Description</ResponsiveDialogDescription>
 *       </ResponsiveDialogHeader>
 *       {children}
 *       <ResponsiveDialogFooter>
 *         <Button>Action</Button>
 *       </ResponsiveDialogFooter>
 *     </ResponsiveDialogContent>
 *   </ResponsiveDialog>
 */

import * as React from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { Icon } from './icon'
import { faXmark } from '@awesome.me/kit-e215ba4646/icons/sharp/light'

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog'

import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from './drawer'

// ---------------------------------------------------------------------------
// Context to share mobile state with sub-components
// ---------------------------------------------------------------------------
interface ResponsiveState {
  isMobile: boolean
  /** Whether the mobile drawer allows swipe-dismiss (drives the drag handle). */
  dismissible: boolean
  /** Controlled-close callback — the X uses this so it works even when
   *  `dismissible` is false (Vaul blocks its own DrawerClose in that mode). */
  onOpenChange?: (open: boolean) => void
}
const ResponsiveContext = React.createContext<ResponsiveState>({
  isMobile: false,
  dismissible: false,
})

function useResponsive() {
  return React.useContext(ResponsiveContext)
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
interface ResponsiveDialogProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: React.ReactNode
  /**
   * Mobile only: allow swipe-down / overlay-tap to dismiss the drawer.
   * **Default `false`** — an accidental swipe on a form loses input, so drawers
   * close via the X (top-right) or an explicit Cancel instead. Opt back in with
   * `dismissible` for throwaway drawers where a swipe-to-close is desirable. No
   * effect on the desktop Dialog (which keeps its own X + overlay/Esc close).
   */
  dismissible?: boolean
}

function ResponsiveDialog({
  open,
  onOpenChange,
  children,
  dismissible = false,
}: ResponsiveDialogProps) {
  const isMobile = useIsMobile()

  // Don't mount Drawer/Dialog root when closed — Vaul attaches document-level
  // touch event listeners even when closed, which can block button taps
  // when multiple Drawer roots exist on the same page.
  if (!open) return null

  return (
    <ResponsiveContext.Provider value={{ isMobile, dismissible, onOpenChange }}>
      {isMobile ? (
        <Drawer
          open={open}
          onOpenChange={onOpenChange}
          dismissible={dismissible}
        >
          {children}
        </Drawer>
      ) : (
        <Dialog open={open} onOpenChange={onOpenChange}>
          {children}
        </Dialog>
      )}
    </ResponsiveContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
interface ResponsiveDialogContentProps {
  className?: string
  children: React.ReactNode
  /** Prevent closing when clicking outside (desktop Dialog only) */
  onInteractOutside?: (e: Event) => void
  /** Prevent closing on Escape key (desktop Dialog only) */
  onEscapeKeyDown?: (e: KeyboardEvent) => void
  /**
   * Mobile only: the child manages its own scroll + footer layout (e.g. the
   * scan wizard's own flex column). The drawer then fills the height and adds
   * NO internal scroll wrapper, so the child's own footer pins to the bottom.
   * Leave off for ordinary forms — those get the built-in scroll body + a
   * pinned `ResponsiveDialogFooter`.
   */
  fillHeight?: boolean
}

function ResponsiveDialogContent({
  className,
  children,
  onInteractOutside,
  onEscapeKeyDown,
  fillHeight = false,
}: ResponsiveDialogContentProps) {
  const { isMobile, dismissible, onOpenChange } = useResponsive()

  // The X close — with swipe-dismiss off by default, this (or an in-content
  // Cancel) is how the user exits. Uses the controlled `onOpenChange(false)`
  // rather than Vaul's `DrawerClose`, which Vaul disables when
  // `dismissible={false}` — otherwise the X would do nothing.
  const drawerClose = (
    <button
      type="button"
      aria-label="Close"
      onClick={() => onOpenChange?.(false)}
      className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon icon={faXmark} className="h-5 w-5" />
      <span className="sr-only">Close</span>
    </button>
  )

  if (isMobile) {
    // Self-managing content (fillHeight): give the drawer a definite height and
    // no scroll wrapper so the child's own flex column pins its footer.
    if (fillHeight) {
      return (
        <DrawerContent
          showHandle={dismissible}
          className={cn(
            'h-[92dvh]',
            className,
            'flex flex-col overflow-hidden rounded-t-2xl'
          )}
        >
          {drawerClose}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {children}
          </div>
        </DrawerContent>
      )
    }

    // Ordinary form: split out the ResponsiveDialogFooter so it pins to the
    // bottom as a full-width bar; everything else scrolls in the body. The
    // handle is hidden unless swipe-dismiss is on.
    const childArray = React.Children.toArray(children)
    const footer = childArray.find(
      c => React.isValidElement(c) && c.type === ResponsiveDialogFooter
    )
    const body = childArray.filter(
      c => !(React.isValidElement(c) && c.type === ResponsiveDialogFooter)
    )
    return (
      <DrawerContent
        showHandle={dismissible}
        className={cn(
          'max-h-[90vh]',
          className,
          'flex flex-col overflow-hidden rounded-t-2xl'
        )}
      >
        {drawerClose}
        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{body}</div>
        {footer && (
          <div className="shrink-0 border-t bg-background px-4 py-3">
            {footer}
          </div>
        )}
      </DrawerContent>
    )
  }

  return (
    <DialogContent
      className={className}
      onInteractOutside={onInteractOutside}
      onEscapeKeyDown={onEscapeKeyDown}
    >
      {children}
    </DialogContent>
  )
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
function ResponsiveDialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { isMobile } = useResponsive()
  return isMobile ? (
    <DrawerHeader className={cn('px-0', className)} {...props} />
  ) : (
    <DialogHeader className={className} {...props} />
  )
}

// ---------------------------------------------------------------------------
// Title
// ---------------------------------------------------------------------------
function ResponsiveDialogTitle({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogTitle>) {
  const { isMobile } = useResponsive()
  return isMobile ? (
    <DrawerTitle className={className} {...props} />
  ) : (
    <DialogTitle className={className} {...props} />
  )
}

// ---------------------------------------------------------------------------
// Description
// ---------------------------------------------------------------------------
function ResponsiveDialogDescription({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogDescription>) {
  const { isMobile } = useResponsive()
  return isMobile ? (
    <DrawerDescription className={className} {...props} />
  ) : (
    <DialogDescription className={className} {...props} />
  )
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------
function ResponsiveDialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { isMobile } = useResponsive()
  return isMobile ? (
    // Padding comes from the pinned footer bar in ResponsiveDialogContent, so
    // the footer itself is padding-free (p-0) — buttons fill the width.
    <DrawerFooter
      className={cn('flex-row gap-2 p-0 [&>button]:flex-1', className)}
      {...props}
    />
  ) : (
    <DialogFooter className={className} {...props} />
  )
}

// ---------------------------------------------------------------------------
// Close
// ---------------------------------------------------------------------------
function ResponsiveDialogClose({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogClose>) {
  const { isMobile } = useResponsive()
  return isMobile ? (
    <DrawerClose className={className} {...props} />
  ) : (
    <DialogClose className={className} {...props} />
  )
}

// ---------------------------------------------------------------------------
// Trigger
// ---------------------------------------------------------------------------
function ResponsiveDialogTrigger({
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof DialogTrigger>) {
  const { isMobile } = useResponsive()
  return isMobile ? (
    <DrawerTrigger className={className} {...props} />
  ) : (
    <DialogTrigger className={className} {...props} />
  )
}

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogClose,
  ResponsiveDialogTrigger,
}
